import {
  Button,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
  TextInput,
  Toggle,
  Tooltip,
} from '@carbon/react';
import { TrashCan } from '@carbon/react/icons';
import { ColorSwatch } from 'components/color-picker';
import { NumericField } from 'pages/pricing/shared/numeric-field';
import { MultiDatePicker } from 'pages/pricing/shared/multi-date-picker';
import type { ChartType, Metric, Mode, VizSeries } from './view-model';
import styles from './market-data.module.scss';

// The instrument dropdown only needs id + name; Curve and Contract both satisfy this shape.
interface InstrumentOption {
  id: number;
  name: string;
}

const PENDING_RELEASE =
  'This series type will be enabled in a followup release.';

// Tenor Fixed / Tenor Relative stay visible but unselectable until they ship. RadioButtonGroup
// clones its direct children to inject name/checked/onChange, so the Tooltip can't wrap the
// RadioButton itself — it goes inside labelText (a ReactNode), which is what the user hovers.
// `description` (aria-describedby) rather than `label` (aria-labelledby): the latter would
// replace the radio's accessible name, so a screen reader would announce the "coming soon" text
// instead of "Tenor Fixed".
// `autoAlign` is load-bearing, not polish: the config rail sets `overflow-y: auto`, which
// computes `overflow-x` to `auto` too, so a statically-positioned popover gets clipped at the
// rail's right edge. autoAlign switches Carbon's popover to `strategy: 'fixed'` (plus flip),
// which escapes the clipping ancestor so the tooltip can spill over the chart panel.
const pendingLabel = (text: string) => (
  <Tooltip description={PENDING_RELEASE} align="right" autoAlign>
    <span tabIndex={0}>{text}</span>
  </Tooltip>
);

interface SeriesEditorProps {
  series: VizSeries;
  readOnly: boolean;
  curves: InstrumentOption[];
  contracts: InstrumentOption[];
  asOfDates: string[];
  onChange: (patch: Partial<VizSeries>) => void;
  onRemove: () => void;
}

// The editor body for a single Price-Viz series — the contents of its accordion item. The
// accordion header (color dot + name + open/close) stays with the parent so it owns the
// Accordion's open state; this component only edits the series and calls back with a patch.
export function SeriesEditor({
  series: s,
  readOnly,
  curves,
  contracts,
  asOfDates,
  onChange,
  onRemove,
}: SeriesEditorProps) {
  // The series inputs (but not the accordion header above) are disabled on a read-only view, so
  // it can still be expanded to inspect the details.
  return (
    <fieldset className={styles.configFieldset} disabled={readOnly}>
      <div className={styles.seriesBody}>
        <div className={styles.seriesHead}>
          <ColorSwatch
            color={s.color}
            onChange={(color) => onChange({ color })}
          />
          <TextInput
            id={`pv-name-${s.id}`}
            size="sm"
            labelText="Name"
            hideLabel
            value={s.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>

        <RadioButtonGroup
          name={`pv-mode-${s.id}`}
          legendText="Define series by"
          valueSelected={s.mode}
          onChange={(value) =>
            onChange({
              mode: value as Mode,
              instrument: '',
              metric: value === 'contract' ? 'price' : s.metric,
            })
          }
        >
          <RadioButton
            id={`pv-mode-curve-${s.id}`}
            labelText="Curve"
            value="curve"
          />
          <RadioButton
            id={`pv-mode-contract-${s.id}`}
            labelText="Contract"
            value="contract"
          />
        </RadioButtonGroup>

        <Select
          id={`pv-instrument-${s.id}`}
          size="sm"
          labelText="Instrument"
          value={s.instrument}
          onChange={(e) => onChange({ instrument: e.target.value })}
        >
          <SelectItem value="" text="Select…" />
          {(s.mode === 'curve' ? curves : contracts).map((c) => (
            <SelectItem key={c.id} value={String(c.id)} text={c.name} />
          ))}
        </Select>

        <RadioButtonGroup
          name={`pv-type-${s.id}`}
          legendText="Series configuration"
          orientation="vertical"
          valueSelected={s.chartType}
          onChange={(value) => {
            const chartType = value as ChartType;
            onChange(
              chartType === 'settles_forwards'
                ? { chartType, metric: 'price' }
                : { chartType }
            );
          }}
        >
          <RadioButton
            id={`pv-type-sf-${s.id}`}
            labelText="Settles & forwards"
            value="settles_forwards"
          />
          <RadioButton
            id={`pv-type-tenor-${s.id}`}
            labelText={pendingLabel('Tenor Fixed')}
            value="tenor"
            disabled
          />
          <RadioButton
            id={`pv-type-forward-${s.id}`}
            labelText={pendingLabel('Tenor Relative')}
            value="forward"
            disabled
          />
        </RadioButtonGroup>

        {s.chartType !== 'settles_forwards' && s.mode === 'curve' && (
          <Select
            id={`pv-metric-${s.id}`}
            size="sm"
            labelText="Metric"
            value={s.metric}
            onChange={(e) => onChange({ metric: e.target.value as Metric })}
          >
            <SelectItem value="price" text="Price" />
            <SelectItem value="vol" text="Volatility (σ)" />
          </Select>
        )}

        {s.chartType === 'settles_forwards' && (
          <>
            <MultiDatePicker
              id={`pv-asofs-${s.id}`}
              size="sm"
              labelText="Forward as-of Dates"
              placeholder="Select as-of dates"
              value={s.asOfDates}
              enabledDates={asOfDates}
              onChange={(dates) => onChange({ asOfDates: dates })}
            />
            <Toggle
              id={`pv-risk-${s.id}`}
              size="sm"
              labelText=""
              labelA="Add risk (±σ) off"
              labelB="Add risk (±σ) on"
              toggled={s.addRisk}
              onToggle={(on) => onChange({ addRisk: on })}
            />
          </>
        )}

        {s.chartType === 'tenor' && (
          <>
            <TextInput
              id={`pv-ts-${s.id}`}
              size="sm"
              type="month"
              labelText="From month"
              value={s.tenorStart}
              onChange={(e) => onChange({ tenorStart: e.target.value })}
            />
            <TextInput
              id={`pv-te-${s.id}`}
              size="sm"
              type="month"
              labelText="To month"
              value={s.tenorEnd}
              onChange={(e) => onChange({ tenorEnd: e.target.value })}
            />
          </>
        )}

        {s.chartType === 'forward' && (
          <>
            <NumericField
              id={`pv-ff-${s.id}`}
              size="sm"
              label="From (months ahead)"
              allowNegative={false}
              allowDecimal={false}
              min={1}
              max={60}
              value={s.fwdFrom}
              onValueChange={(value) => onChange({ fwdFrom: value })}
            />
            <NumericField
              id={`pv-ft-${s.id}`}
              size="sm"
              label="To (months ahead)"
              allowNegative={false}
              allowDecimal={false}
              min={1}
              max={60}
              value={s.fwdTo}
              onValueChange={(value) => onChange({ fwdTo: value })}
            />
          </>
        )}

        {/* Delete sits at the bottom of the series config (mockui layout). */}
        <div className={styles.seriesFooter}>
          <Button
            hasIconOnly
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            iconDescription="Delete series"
            tooltipPosition="top"
            tooltipAlignment="end"
            onClick={onRemove}
          />
        </div>
      </div>
    </fieldset>
  );
}
